/**
 * Ring buffer of the last 10 user actions (navigations + labeled clicks),
 * mirrored to sessionStorage so a bug report can attach recent context.
 * Framework-free — wired to the router/DOM by ActionTrailListener and to the
 * signed-in account by App (bindTrailToUser on every auth event).
 *
 * The persisted copy belongs to the account that produced it. At startup a
 * stored trail of an older format, or with no owner, is removed; one stored
 * under a different user than the first signed-in user of this page load is
 * dropped. Only the owner's own trail ever comes back after a reload or an
 * OAuth redirect, so nothing captured under another account, or before this
 * version, can reach a bug report.
 */

const STORAGE_KEY = 'tk_action_trail';
const STORAGE_VERSION = 2;
const MAX_ENTRIES = 10;

export interface TrailAction {
  type: 'nav' | 'click';
  label: string;
  at: string;
}

interface StoredTrail {
  v: typeof STORAGE_VERSION;
  owner: string;
  entries: TrailAction[];
}

function removeStorage(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // sessionStorage unavailable — nothing persisted to remove.
  }
}

/** The persisted trail if it is this version and has an owner; anything
 *  else is removed from storage and never loaded. */
function readStorage(): StoredTrail | null {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (
      parsed?.v === STORAGE_VERSION &&
      typeof parsed.owner === 'string' &&
      parsed.owner !== '' &&
      Array.isArray(parsed.entries)
    ) {
      return parsed as StoredTrail;
    }
  } catch {
    // unreadable: removed below like any other unusable copy
  }
  removeStorage();
  return null;
}

// Held back until the signed-in user of this page load is known.
let persisted: StoredTrail | null = readStorage();
let bound = false;
let owner: string | null = null;
let trail: TrailAction[] = [];

function writeStorage(): void {
  if (!bound) return; // the persisted copy is untouched until its owner is known
  if (!owner) {
    removeStorage();
    return;
  }
  try {
    const stored: StoredTrail = { v: STORAGE_VERSION, owner, entries: trail };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // sessionStorage unavailable (private mode, quota, etc.) — in-memory buffer still works.
  }
}

const MAX_LABEL_LENGTH = 60;

export function record(action: TrailAction): void {
  const entry: TrailAction = { ...action, label: action.label.slice(0, MAX_LABEL_LENGTH) };
  trail = [...trail, entry].slice(-MAX_ENTRIES);
  writeStorage();
}

export function getTrail(): TrailAction[] {
  return trail;
}

/**
 * Binds the trail to the signed-in user (null when signed out); App calls it
 * on every auth event. The first call of a page load restores the persisted
 * trail only if this user produced it. After that, a different user (or
 * sign-out) empties the trail, so one account's actions never reach a bug
 * report filed under another.
 */
export function bindTrailToUser(userId: string | null): void {
  if (!bound) {
    bound = true;
    if (userId && persisted?.owner === userId) {
      trail = [...persisted.entries, ...trail].slice(-MAX_ENTRIES);
    }
    persisted = null;
  } else if (userId !== owner) {
    trail = [];
  }
  owner = userId;
  if (!userId) trail = [];
  writeStorage();
}
